import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Heart, ArrowLeft, Calendar, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme-specific colors
const THEME_COLORS = {
  healing: {
    bg: '#E7C6C2',
    gradient: 'from-[#E7C6C2]/90 via-[#F7E5E5]/60 to-[#E7C6C2]/90'
  },
  strength: {
    bg: '#6C4CA2',
    gradient: 'from-[#6C4CA2]/90 via-[#8B6BB8]/60 to-[#6C4CA2]/90'
  },
  sisterhood: {
    bg: '#AAB49F',
    gradient: 'from-[#AAB49F]/90 via-[#C8D1C0]/60 to-[#AAB49F]/90'
  },
  empowerment: {
    bg: '#7C9069',
    gradient: 'from-[#7C9069]/90 via-[#AAB49F]/60 to-[#7C9069]/90'
  }
};

const DailyQuotePage = () => {
  const navigate = useNavigate();
  const [dailyQuote, setDailyQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journalEntry, setJournalEntry] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  const [userSessionId] = useState(() => {
    let sessionId = localStorage.getItem('user_session_id');
    if (!sessionId) {
      sessionId = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('user_session_id', sessionId);
    }
    return sessionId;
  });
  
  const videoRef = useRef(null);

  useEffect(() => {
    fetchDailyQuote();
  }, []);

  const fetchDailyQuote = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/daily-quote/${userSessionId}`);
      setDailyQuote(response.data);
      setJournalEntry(response.data.journal_entry || '');
      setIsFavorited(response.data.is_favorited || false);
    } catch (error) {
      console.error('Error fetching daily quote:', error);
      toast.error('Failed to load today\'s quote');
    } finally {
      setLoading(false);
    }
  };

  const saveJournalEntry = async () => {
    if (!journalEntry.trim()) return;
    
    try {
      await axios.post(`${API}/journal/quote?user_session_id=${userSessionId}`, {
        quote_id: dailyQuote.quote.id,
        journal_entry: journalEntry
      });
      toast.success('Journal entry saved ✍️');
    } catch (error) {
      console.error('Error saving journal:', error);
      toast.error('Failed to save journal entry');
    }
  };

  const toggleFavorite = async () => {
    try {
      const response = await axios.post(`${API}/reels/favorite`, {
        user_session_id: userSessionId,
        reel_id: dailyQuote.quote.id
      });
      
      setIsFavorited(response.data.status === 'added');
      toast.success(response.data.status === 'added' ? 'Added to favorites ❤️' : 'Removed from favorites');
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite');
    }
  };

  // Auto-save journal entry after 2 seconds of no typing
  useEffect(() => {
    if (!dailyQuote) return;
    
    const timer = setTimeout(() => {
      if (journalEntry !== (dailyQuote.journal_entry || '')) {
        saveJournalEntry();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [journalEntry]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2E2536]">
        <div className="text-center">
          <Calendar className="w-12 h-12 text-[#C8B5E0] mx-auto mb-4 animate-pulse" />
          <p className="text-[#EDE8F3] text-lg">Loading today's whisper...</p>
        </div>
      </div>
    );
  }

  if (!dailyQuote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2E2536]">
        <div className="text-center px-6">
          <p className="text-[#EDE8F3] text-xl">No quote available</p>
        </div>
      </div>
    );
  }

  const currentThemeColors = THEME_COLORS[dailyQuote.category] || THEME_COLORS.empowerment;
  const nextUnlockDate = new Date(dailyQuote.next_unlock);
  const hours = Math.max(0, Math.floor((nextUnlockDate - new Date()) / (1000 * 60 * 60)));

  return (
    <div className="min-h-screen bg-[#2E2536]">
      {/* Quote Section */}
      <div className="relative h-[60vh] w-full overflow-hidden">
        {/* Video Background */}
        <video
          ref={videoRef}
          key={dailyQuote.quote.id}
          src={dailyQuote.quote.background_video_url}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
        />
        
        {/* Theme-specific gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-b ${currentThemeColors.gradient}`}></div>

        {/* Content */}
        <div className="relative h-full flex flex-col justify-between p-6 text-white">
          {/* Top Bar */}
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <div 
              className="backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2"
              style={{ backgroundColor: `${currentThemeColors.bg}CC` }}
            >
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Day {dailyQuote.current_day}</span>
            </div>

            <Button
              onClick={toggleFavorite}
              variant="ghost"
              size="icon"
              className="rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30"
            >
              <Heart
                className={`w-6 h-6 ${isFavorited ? 'fill-[#E7C6C2] text-[#E7C6C2]' : 'text-white'}`}
              />
            </Button>
          </div>

          {/* Center Quote */}
          <div className="flex-1 flex items-center justify-center px-8">
            <div className="text-center max-w-2xl">
              <div 
                className="backdrop-blur-sm px-3 py-1 rounded-full mb-4 inline-block"
                style={{ backgroundColor: `${currentThemeColors.bg}80` }}
              >
                <span className="text-xs font-medium capitalize text-white">{dailyQuote.category}</span>
              </div>
              <p className="text-xl sm:text-2xl lg:text-3xl font-semibold leading-relaxed text-white drop-shadow-2xl" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                {dailyQuote.quote.quote}
              </p>
            </div>
          </div>

          {/* Next Unlock Info */}
          <div className="text-center">
            <p className="text-sm text-white/70">
              Next quote unlocks in {hours} hours
            </p>
          </div>
        </div>
      </div>

      {/* Journal Section */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-[#3E324D] rounded-2xl p-6 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-[#C8B5E0]" />
            <h2 className="text-xl font-semibold text-[#EDE8F3]">
              What did this make you feel today?
            </h2>
          </div>
          
          <textarea
            value={journalEntry}
            onChange={(e) => setJournalEntry(e.target.value)}
            placeholder="Your thoughts are safe here... Write as much or as little as you need."
            className="w-full min-h-[200px] bg-[#2E2536] text-[#EDE8F3] rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-[#C8B5E0] resize-none placeholder-[#6E6275]"
          />
          
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-[#C8B5E0]">
              {journalEntry.length > 0 ? 'Auto-saving...' : 'Start typing to save'}
            </p>
            
            <Button
              onClick={() => navigate('/journey')}
              variant="ghost"
              className="text-[#C8B5E0] hover:bg-[#C8B5E0]/20"
            >
              Your Journey
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyQuotePage;
